import type { FieldType, Params, SheetKey } from '@/lib/data/types'

/**
 * Workbook schema.
 *
 * Sheets and columns are recognized by synonyms, so files coming from
 * different areas keep working when a header reads "Responsable", "Owner" or
 * "Líder". Synonyms are compared after normalization (no accents, no case,
 * no punctuation, no stop words, light plural stemming), see `normalize.ts`.
 */

export interface FieldDef {
    key: string
    type: FieldType
    synonyms: string[]
    /** Reported in the data quality tab when it is missing */
    important?: boolean
}

export interface SheetDef {
    key: SheetKey
    required: boolean
    synonyms: string[]
    /** Fields that identify the sheet when its name is not recognized */
    fingerprint: string[]
    /** Groups of fields: a row is valid only if every group has a value */
    rowRequires: string[][]
    fields: FieldDef[]
}

const ID = ['id', 'clave', 'folio', 'codigo', 'identificador', 'no', 'num', 'numero', 'id iniciativa', 'id proyecto', 'initiative id', 'project id']
const DIVISION = ['division', 'direccion', 'vicepresidencia', 'vp', 'area', 'direccion general', 'division negocio']
const BU = ['bu', 'unidad de negocio', 'unidad negocio', 'business unit', 'un', 'negocio', 'linea de negocio', 'subsidiaria']
const OWNER = ['responsable', 'lider', 'lider proyecto', 'owner', 'dueno', 'encargado', 'pm', 'gerente proyecto', 'project manager', 'asignado', 'assignee', 'responsable iniciativa']
const PROGRAM = ['programa', 'program', 'programa estrategico', 'pilar', 'linea estrategica', 'eje']
const DOMAIN = ['dominio', 'domain', 'dominio de seguridad', 'capacidad', 'area de seguridad', 'pilar seguridad', 'tema']
const CRITICALITY = ['criticidad', 'prioridad', 'criticality', 'priority', 'nivel criticidad', 'nivel de prioridad', 'importancia']
const STATUS = ['estatus', 'estado', 'status', 'situacion']
const PROGRESS = ['avance', 'pct avance', 'avance real', 'porcentaje avance', 'porcentaje de avance', 'progreso', 'progress', 'pct real', 'avance actual', 'real', 'completado', 'pct completado', 'actual progress']
const PLANNED = ['avance plan', 'avance planeado', 'avance esperado', 'avance programado', 'pct plan', 'pct planeado', 'plan', 'planeado', 'esperado', 'programado', 'planned', 'planned progress', 'baseline', 'meta avance']
const START = ['fecha inicio', 'inicio', 'fecha de inicio', 'start', 'start date', 'fecha arranque', 'arranque', 'inicio plan']
const END = ['fecha fin', 'fin', 'fecha de fin', 'fecha termino', 'termino', 'fecha compromiso', 'fecha objetivo', 'fecha entrega', 'end', 'end date', 'due date', 'fin plan', 'fecha limite', 'deadline']
const DUE = ['fecha compromiso', 'fecha limite', 'vencimiento', 'fecha vencimiento', 'fecha objetivo', 'fecha fin', 'due date', 'fecha entrega', 'deadline']
const CLOSED = ['fecha cierre', 'fecha cumplimiento', 'fecha atencion', 'fecha cerrado', 'closed date', 'cierre']
const PERIOD = ['periodo', 'mes', 'fecha', 'period', 'month', 'anio mes', 'semana', 'corte']
const DIRECTION = ['sentido', 'polaridad', 'tendencia deseada', 'mejor si', 'direction', 'tipo meta', 'mejor']

const dims = (options: { criticality?: string[], important?: boolean } = {}): FieldDef[] => [
    { key: 'division', type: 'text', synonyms: DIVISION, important: options.important ?? true },
    { key: 'bu', type: 'text', synonyms: BU, important: options.important ?? true },
    { key: 'owner', type: 'text', synonyms: OWNER, important: options.important ?? true },
    { key: 'program', type: 'text', synonyms: PROGRAM },
    { key: 'domain', type: 'text', synonyms: DOMAIN },
    { key: 'criticality', type: 'text', synonyms: options.criticality ?? CRITICALITY }
]

export const SHEETS: SheetDef[] = [
    {
        key: 'portfolio',
        required: true,
        synonyms: ['portafolio vigente', 'portafolio', 'portfolio', 'portafolio actual', 'iniciativas', 'iniciativas vigentes', 'proyectos', 'cartera', 'cartera proyectos', 'current portfolio'],
        fingerprint: ['name', 'status', 'progress', 'end', 'program'],
        rowRequires: [['name', 'id']],
        fields: [
            { key: 'id', type: 'text', synonyms: ID, important: true },
            { key: 'name', type: 'text', synonyms: ['nombre', 'iniciativa', 'nombre iniciativa', 'nombre de la iniciativa', 'proyecto', 'nombre proyecto', 'nombre del proyecto', 'initiative', 'initiative name', 'project', 'project name', 'name', 'titulo', 'title'], important: true },
            ...dims(),
            { key: 'status', type: 'text', synonyms: STATUS, important: true },
            { key: 'progress', type: 'percent', synonyms: PROGRESS, important: true },
            { key: 'planned', type: 'percent', synonyms: PLANNED },
            { key: 'start', type: 'date', synonyms: START, important: true },
            { key: 'end', type: 'date', synonyms: END, important: true },
            { key: 'tower', type: 'text', synonyms: ['torre', 'tower', 'torre de servicio'] },
            { key: 'health', type: 'text', synonyms: ['salud', 'semaforo', 'rag', 'health', 'estado salud', 'semaforizacion'] },
            { key: 'budget', type: 'number', synonyms: ['presupuesto', 'monto', 'inversion', 'budget', 'costo', 'monto aprobado'] },
            { key: 'notes', type: 'text', synonyms: ['comentarios', 'comentario', 'notas', 'observaciones', 'notes', 'comments', 'siguientes pasos', 'proximos pasos'] }
        ]
    },
    {
        key: 'history',
        required: false,
        synonyms: ['portafolio historico', 'historico', 'historial', 'cortes', 'cortes semanales', 'historico portafolio', 'history', 'portfolio history', 'snapshots', 'bitacora avance', 'seguimiento semanal'],
        fingerprint: ['cut', 'progress', 'id'],
        rowRequires: [['cut'], ['id', 'name'], ['progress', 'status']],
        fields: [
            { key: 'cut', type: 'date', synonyms: ['fecha corte', 'corte', 'fecha de corte', 'semana corte', 'snapshot', 'cut', 'cut date', 'fecha', 'fecha reporte', 'periodo corte', 'week', 'semana'], important: true },
            { key: 'id', type: 'text', synonyms: ID, important: true },
            { key: 'name', type: 'text', synonyms: ['nombre', 'iniciativa', 'nombre iniciativa', 'proyecto', 'initiative', 'name', 'titulo'] },
            { key: 'progress', type: 'percent', synonyms: PROGRESS, important: true },
            { key: 'planned', type: 'percent', synonyms: PLANNED },
            { key: 'status', type: 'text', synonyms: STATUS },
            { key: 'end', type: 'date', synonyms: END },
            // Dimensions come from the current portfolio; here they only cover rows that left it
            ...dims({ important: false })
        ]
    },
    {
        key: 'compliance',
        required: false,
        synonyms: ['cumplimiento seguimiento', 'cumplimiento', 'seguimiento cumplimiento', 'compliance', 'compliance tracking', 'regulatorio', 'requisitos'],
        fingerprint: ['requirement', 'framework', 'status', 'due'],
        rowRequires: [['requirement', 'id']],
        fields: [
            { key: 'id', type: 'text', synonyms: [...ID, 'id requisito', 'id control'] },
            { key: 'requirement', type: 'text', synonyms: ['requisito', 'requerimiento', 'control', 'obligacion', 'actividad', 'nombre', 'descripcion', 'requirement', 'control name', 'tarea', 'observacion'], important: true },
            { key: 'framework', type: 'text', synonyms: ['marco', 'marco normativo', 'norma', 'normativa', 'regulacion', 'regulador', 'framework', 'estandar', 'standard', 'fuente', 'origen'], important: true },
            ...dims(),
            { key: 'status', type: 'text', synonyms: STATUS, important: true },
            { key: 'progress', type: 'percent', synonyms: PROGRESS, important: true },
            { key: 'due', type: 'date', synonyms: DUE, important: true },
            { key: 'closed', type: 'date', synonyms: CLOSED },
            { key: 'evidence', type: 'text', synonyms: ['evidencia', 'evidence', 'soporte'] }
        ]
    },
    {
        key: 'csf',
        required: false,
        synonyms: ['heatmap csf', 'csf', 'nist csf', 'nist', 'madurez csf', 'madurez', 'heatmap', 'maturity', 'nist heatmap'],
        fingerprint: ['fn', 'category', 'current', 'target'],
        rowRequires: [['category', 'fn'], ['current', 'target']],
        fields: [
            { key: 'fn', type: 'text', synonyms: ['funcion', 'function', 'funcion csf', 'funcion nist'], important: true },
            { key: 'category', type: 'text', synonyms: ['categoria', 'category', 'id categoria', 'codigo categoria', 'subcategoria', 'clave', 'id'], important: true },
            { key: 'categoryName', type: 'text', synonyms: ['nombre categoria', 'descripcion', 'nombre', 'category name', 'descripcion categoria'] },
            { key: 'current', type: 'number', synonyms: ['madurez actual', 'actual', 'nivel actual', 'current', 'current maturity', 'madurez', 'puntaje actual', 'calificacion actual', 'estado actual'], important: true },
            { key: 'target', type: 'number', synonyms: ['madurez objetivo', 'objetivo', 'nivel objetivo', 'meta', 'target', 'target maturity', 'deseado', 'puntaje objetivo', 'estado objetivo'], important: true },
            { key: 'division', type: 'text', synonyms: DIVISION },
            { key: 'bu', type: 'text', synonyms: BU },
            { key: 'owner', type: 'text', synonyms: OWNER },
            { key: 'period', type: 'period', synonyms: PERIOD }
        ]
    },
    {
        key: 'design',
        required: false,
        synonyms: ['diseno arquitectura', 'diseno', 'arquitectura', 'diseno y arquitectura', 'arquitectura seguridad', 'diseno seguridad', 'security design', 'design', 'architecture'],
        fingerprint: ['activity', 'hoursPlanned', 'hoursActual', 'project'],
        rowRequires: [['activity', 'id']],
        fields: [
            { key: 'id', type: 'text', synonyms: ID },
            { key: 'activity', type: 'text', synonyms: ['actividad', 'nombre', 'nombre actividad', 'solicitud', 'revision', 'entregable', 'activity', 'tarea', 'descripcion', 'servicio'], important: true },
            { key: 'project', type: 'text', synonyms: ['proyecto', 'iniciativa', 'aplicacion', 'sistema', 'project', 'solucion'] },
            { key: 'type', type: 'text', synonyms: ['tipo', 'tipo actividad', 'tipo de actividad', 'tipo revision', 'type', 'clasificacion'] },
            ...dims(),
            { key: 'status', type: 'text', synonyms: STATUS, important: true },
            { key: 'progress', type: 'percent', synonyms: PROGRESS, important: true },
            { key: 'start', type: 'date', synonyms: START },
            { key: 'end', type: 'date', synonyms: END, important: true },
            { key: 'hoursPlanned', type: 'number', synonyms: ['horas estimadas', 'horas plan', 'horas planeadas', 'estimado horas', 'horas presupuestadas', 'estimated hours', 'esfuerzo estimado', 'horas estimado'], important: true },
            { key: 'hoursActual', type: 'number', synonyms: ['horas reales', 'horas', 'horas ejecutadas', 'horas consumidas', 'horas invertidas', 'actual hours', 'esfuerzo real', 'horas real'], important: true }
        ]
    },
    {
        key: 'findings',
        required: false,
        synonyms: ['diseno hallazgos', 'hallazgos', 'red team', 'hallazgos red team', 'findings', 'red team findings', 'vulnerabilidades', 'pentest', 'hallazgos diseno'],
        fingerprint: ['title', 'criticality', 'detected', 'closed'],
        rowRequires: [['title', 'id']],
        fields: [
            { key: 'id', type: 'text', synonyms: [...ID, 'id hallazgo', 'folio hallazgo'] },
            { key: 'title', type: 'text', synonyms: ['hallazgo', 'titulo', 'descripcion', 'vulnerabilidad', 'nombre', 'finding', 'title', 'nombre hallazgo', 'observacion'], important: true },
            ...dims({ criticality: ['criticidad', 'severidad', 'severity', 'riesgo', 'nivel', 'nivel de riesgo', 'impacto', 'prioridad', 'criticality'] }),
            { key: 'status', type: 'text', synonyms: [...STATUS, 'estatus remediacion'], important: true },
            { key: 'detected', type: 'date', synonyms: ['fecha deteccion', 'fecha hallazgo', 'fecha identificacion', 'fecha alta', 'detectado', 'fecha reporte', 'fecha', 'detected', 'fecha descubrimiento', 'fecha apertura'], important: true },
            { key: 'closed', type: 'date', synonyms: ['fecha cierre', 'cerrado', 'fecha remediacion', 'fecha solucion', 'closed', 'fecha cerrado'] },
            { key: 'due', type: 'date', synonyms: ['fecha compromiso', 'fecha limite', 'sla', 'fecha objetivo', 'vencimiento', 'due date'] },
            { key: 'asset', type: 'text', synonyms: ['activo', 'sistema', 'aplicacion', 'asset', 'componente', 'servidor', 'objetivo prueba', 'host'] },
            { key: 'exercise', type: 'text', synonyms: ['ejercicio', 'campana', 'prueba', 'ejercicio red team', 'exercise', 'engagement', 'operacion'] }
        ]
    },
    {
        key: 'volumetry',
        required: false,
        synonyms: ['volumetrias', 'volumetria', 'indicadores operativos', 'operacion', 'volumetry', 'kpis operativos', 'volumenes', 'indicadores'],
        fingerprint: ['tower', 'indicator', 'period', 'value'],
        rowRequires: [['indicator'], ['period'], ['value']],
        fields: [
            { key: 'tower', type: 'text', synonyms: ['torre', 'tower', 'servicio', 'torre servicio', 'frente'], important: true },
            { key: 'indicator', type: 'text', synonyms: ['indicador', 'metrica', 'kpi', 'concepto', 'indicator', 'metric', 'nombre indicador', 'medicion'], important: true },
            { key: 'period', type: 'period', synonyms: PERIOD, important: true },
            { key: 'value', type: 'number', synonyms: ['valor', 'volumen', 'cantidad', 'total', 'real', 'resultado', 'value', 'volume', 'actual'], important: true },
            { key: 'target', type: 'number', synonyms: ['meta', 'objetivo', 'umbral', 'target', 'sla', 'esperado'] },
            { key: 'unit', type: 'text', synonyms: ['unidad', 'unit', 'unidad medida', 'um'] },
            { key: 'direction', type: 'text', synonyms: DIRECTION },
            { key: 'division', type: 'text', synonyms: DIVISION },
            { key: 'bu', type: 'text', synonyms: BU }
        ]
    },
    {
        key: 'tiers',
        required: false,
        synonyms: ['tiers', 'tier', 'clasificacion tiers', 'criticidad activos', 'activos', 'aplicaciones criticas', 'bia', 'clasificacion criticidad'],
        fingerprint: ['asset', 'tier', 'rto', 'rpo'],
        rowRequires: [['asset', 'id'], ['tier', 'criticality']],
        fields: [
            { key: 'id', type: 'text', synonyms: ID },
            { key: 'asset', type: 'text', synonyms: ['activo', 'aplicacion', 'sistema', 'servicio', 'nombre', 'asset', 'application', 'nombre activo', 'nombre aplicacion', 'proceso'], important: true },
            { key: 'tier', type: 'text', synonyms: ['tier', 'nivel tier', 'clasificacion tier', 'tier criticidad', 'nivel'], important: true },
            ...dims({ criticality: ['criticidad', 'criticality', 'criticidad negocio', 'impacto negocio', 'prioridad'] }),
            { key: 'rto', type: 'text', synonyms: ['rto', 'tiempo recuperacion'] },
            { key: 'rpo', type: 'text', synonyms: ['rpo', 'punto recuperacion'] },
            { key: 'dataClass', type: 'text', synonyms: ['clasificacion datos', 'clasificacion de datos', 'clasificacion informacion', 'datos', 'sensibilidad', 'tipo datos', 'data classification', 'confidencialidad'] }
        ]
    },
    {
        key: 'risks',
        required: false,
        synonyms: ['perfil de riesgos', 'perfil riesgos', 'riesgos', 'risk profile', 'risks', 'matriz riesgos', 'matriz de riesgos', 'registro riesgos', 'riesgo'],
        fingerprint: ['risk', 'probability', 'impact', 'treatment'],
        rowRequires: [['risk', 'id']],
        fields: [
            { key: 'id', type: 'text', synonyms: [...ID, 'id riesgo'] },
            { key: 'risk', type: 'text', synonyms: ['riesgo', 'nombre riesgo', 'descripcion', 'titulo', 'risk', 'nombre', 'escenario', 'descripcion riesgo'], important: true },
            ...dims({ criticality: ['nivel', 'nivel riesgo', 'nivel de riesgo', 'severidad', 'riesgo residual', 'nivel residual', 'calificacion', 'risk level', 'criticidad'] }),
            { key: 'probability', type: 'number', synonyms: ['probabilidad', 'likelihood', 'prob', 'frecuencia', 'probability'], important: true },
            { key: 'impact', type: 'number', synonyms: ['impacto', 'impact', 'severidad impacto', 'consecuencia'], important: true },
            { key: 'score', type: 'number', synonyms: ['puntaje', 'score', 'valor riesgo', 'exposicion', 'puntuacion'] },
            { key: 'status', type: 'text', synonyms: STATUS, important: true },
            { key: 'treatment', type: 'text', synonyms: ['tratamiento', 'plan de accion', 'plan accion', 'respuesta', 'mitigacion', 'estrategia', 'treatment', 'plan mitigacion', 'accion'] },
            { key: 'review', type: 'date', synonyms: ['fecha revision', 'fecha', 'ultima revision', 'fecha evaluacion', 'review date', 'fecha identificacion'] },
            { key: 'trend', type: 'text', synonyms: ['tendencia', 'trend'] }
        ]
    },
    {
        key: 'budget',
        required: false,
        synonyms: ['presupuesto', 'budget', 'presupuesto ciberseguridad', 'opex capex', 'presupuestos'],
        fingerprint: ['line', 'plan', 'actual', 'period'],
        rowRequires: [['line', 'lineId'], ['plan', 'actual']],
        fields: [
            { key: 'lineId', type: 'text', synonyms: ['id', 'id linea', 'clave', 'partida', 'clave presupuestal', 'cuenta', 'id partida'] },
            { key: 'line', type: 'text', synonyms: ['linea', 'concepto', 'linea presupuestal', 'descripcion', 'rubro', 'nombre', 'line', 'partida presupuestal', 'concepto gasto', 'proyecto'], important: true },
            { key: 'category', type: 'text', synonyms: ['categoria', 'tipo', 'capex opex', 'clasificacion', 'tipo gasto', 'category', 'naturaleza'], important: true },
            { key: 'division', type: 'text', synonyms: DIVISION },
            { key: 'bu', type: 'text', synonyms: BU },
            { key: 'owner', type: 'text', synonyms: OWNER },
            { key: 'program', type: 'text', synonyms: PROGRAM },
            { key: 'domain', type: 'text', synonyms: DOMAIN },
            { key: 'period', type: 'period', synonyms: ['periodo', 'mes', 'fecha', 'period', 'month', 'mes presupuesto'], important: true },
            { key: 'plan', type: 'number', synonyms: ['plan', 'presupuesto', 'planeado', 'presupuesto plan', 'budget', 'monto plan', 'presupuesto autorizado', 'autorizado', 'planned', 'monto presupuestado', 'ppto'], important: true },
            { key: 'actual', type: 'number', synonyms: ['ejecutado', 'real', 'ejercido', 'gasto', 'gasto real', 'actual', 'monto real', 'monto ejecutado', 'pagado', 'spent'], important: true },
            { key: 'committed', type: 'number', synonyms: ['comprometido', 'committed', 'compromiso'] },
            { key: 'currency', type: 'text', synonyms: ['moneda', 'currency', 'divisa', 'tipo moneda'], important: true },
            { key: 'vendor', type: 'text', synonyms: ['proveedor', 'vendor', 'supplier'] },
            { key: 'rate', type: 'number', synonyms: ['tipo de cambio', 'tipo cambio', 'tc', 'exchange rate'] }
        ]
    },
    {
        key: 'agreements',
        required: false,
        synonyms: ['acuerdos', 'coordinacion', 'seguimiento acuerdos', 'minutas', 'agreements', 'compromisos', 'acuerdos comite'],
        fingerprint: ['agreement', 'forum', 'due', 'owner'],
        rowRequires: [['agreement', 'id']],
        fields: [
            { key: 'id', type: 'text', synonyms: [...ID, 'id acuerdo'] },
            { key: 'agreement', type: 'text', synonyms: ['acuerdo', 'tema', 'compromiso', 'descripcion', 'asunto', 'agreement', 'accion', 'pendiente', 'descripcion acuerdo', 'nombre'], important: true },
            { key: 'forum', type: 'text', synonyms: ['foro', 'comite', 'sesion', 'reunion', 'origen', 'forum', 'committee', 'instancia', 'mesa'] },
            ...dims(),
            { key: 'status', type: 'text', synonyms: STATUS, important: true },
            { key: 'date', type: 'date', synonyms: ['fecha', 'fecha sesion', 'fecha acuerdo', 'fecha alta', 'fecha reunion', 'fecha registro', 'date'] },
            { key: 'due', type: 'date', synonyms: DUE, important: true },
            { key: 'closed', type: 'date', synonyms: CLOSED }
        ]
    },
    {
        key: 'basics',
        required: false,
        synonyms: ['basicos de seguridad', 'basicos seguridad', 'basicos', 'security basics', 'higiene', 'higiene seguridad', 'controles basicos', 'fundamentos'],
        fingerprint: ['control', 'value', 'target'],
        rowRequires: [['control'], ['value']],
        fields: [
            { key: 'control', type: 'text', synonyms: ['control', 'basico', 'basico seguridad', 'indicador', 'practica', 'medida', 'control basico', 'nombre', 'concepto', 'kpi'], important: true },
            { key: 'value', type: 'number', synonyms: ['valor', 'cobertura', 'pct cumplimiento', 'cumplimiento', 'actual', 'real', 'resultado', 'value', 'pct cobertura', 'avance'], important: true },
            { key: 'target', type: 'number', synonyms: ['meta', 'objetivo', 'target', 'umbral'], important: true },
            { key: 'unit', type: 'text', synonyms: ['unidad', 'unit'] },
            { key: 'direction', type: 'text', synonyms: DIRECTION },
            { key: 'division', type: 'text', synonyms: DIVISION },
            { key: 'bu', type: 'text', synonyms: BU },
            { key: 'owner', type: 'text', synonyms: OWNER },
            { key: 'domain', type: 'text', synonyms: DOMAIN },
            { key: 'period', type: 'period', synonyms: PERIOD }
        ]
    },
    {
        key: 'params',
        required: false,
        synonyms: ['parametros', 'configuracion', 'params', 'settings', 'parameters', 'config', 'catalogo parametros'],
        fingerprint: ['key', 'value'],
        rowRequires: [['key'], ['value']],
        fields: [
            { key: 'key', type: 'text', synonyms: ['parametro', 'clave', 'concepto', 'nombre', 'variable', 'parameter', 'key', 'configuracion'], important: true },
            { key: 'value', type: 'text', synonyms: ['valor', 'value', 'dato'], important: true }
        ]
    }
]

/** Known parameter names inside the parameters sheet. */
export const PARAM_KEYS: Record<keyof Params, string[]> = {
    exchangeRate: ['tipo de cambio', 'tipo cambio', 'tc', 'tc usd mxn', 'usd mxn', 'exchange rate', 'tipo de cambio usd', 'tipo cambio usd mxn', 'tipo de cambio usd mxn'],
    baseCurrency: ['moneda base', 'moneda', 'base currency', 'currency', 'moneda presupuesto'],
    cutDate: ['fecha de corte', 'fecha corte', 'corte', 'cut date', 'fecha referencia', 'fecha de referencia', 'fecha corte vigente'],
    amberPp: ['umbral ambar', 'umbral amarillo', 'amber threshold', 'umbral ambar pp', 'umbral atencion'],
    redPp: ['umbral rojo', 'red threshold', 'umbral rojo pp', 'umbral critico'],
    dueSoonDays: ['dias por vencer', 'dias proximos a vencer', 'due soon days', 'ventana vencimiento', 'dias aviso']
}

export const sheetDef = (key: SheetKey): SheetDef => SHEETS.find(sheet => sheet.key === key) as SheetDef
